import {isFeatureEnabled} from 'feature-flags';
import {OptionType} from 'platform/components';

import {head, isNil} from 'ramda';
import {isValidNumber} from 'ramda-adjunct';

import {getSafely, Nullish} from 'shared/web';

import {isTransmissionExcluded} from '../constants/transmissionOptionsExcluded';
import {FilterFormData} from '../types/FilterFormData';
import {PriceReportType} from '../types/PriceReportType';
import {featureFlags} from './featureFlags';
import {getYearOptions} from './getYearOptions';
import {mapMarketToAdServer} from './mapMarketToAdServer';

const DEFAULT_BODY_STYLE = [
  'CARSTYLE_HATCHBACK',
  'CARSTYLE_LIFTBACK',
  'CARSTYLE_SEDANS_SALOONS',
  'CARSTYLE_COMPACT',
];

const getDefaultValue = (
  value: number,
  options: OptionType<number, string>[],
  couldBeSame = false
) => {
  if (couldBeSame) {
    const foundValue = options.find((option) => parseInt(String(option.value)) === value)?.value;
    if (isValidNumber(foundValue)) {
      return {from: foundValue, to: foundValue};
    }
  }

  const toIndex = options.findIndex((option) => parseInt(String(option.value)) > value);

  const fromValue = getSafely(options, toIndex - 1)?.value;
  const toValue = getSafely(options, toIndex)?.value;

  return {from: fromValue, to: toValue};
};

const getDefaultBodyStyle = (value: string | undefined) => {
  if (isNil(value)) {
    return [];
  }

  return DEFAULT_BODY_STYLE.includes(value) ? DEFAULT_BODY_STYLE : [value];
};

const getDefaultServer = (priceReport: PriceReportType | Nullish) => {
  if (isNil(priceReport?.market) || !isFeatureEnabled(featureFlags.PR_MULTI_SERVERS)) {
    return undefined;
  }

  const adServers = mapMarketToAdServer(priceReport.market);
  const isMultiServers = adServers.length > 1;

  return isMultiServers ? [head(adServers)!] : undefined;
};

export const getDefaultValues = (priceReport: PriceReportType | Nullish): FilterFormData => ({
  address: null,
  radius: null,
  title: null,
  showSoldIn30: true,
  showSoldIn90: true,
  showStockCars: true,
  priceGte: null,
  priceLte: null,
  isVatDeductible: [],
  firstRegistrationOnYearGte: priceReport?.vehicle
    ? getDefaultValue(priceReport.vehicle.firstRegistrationOnYear, getYearOptions(), true)?.from
    : null,
  firstRegistrationOnYearLte: priceReport?.vehicle
    ? getDefaultValue(priceReport.vehicle.firstRegistrationOnYear, getYearOptions(), true)?.to
    : null,
  mileageGte: null,
  mileageLte: null,
  daysInAdvertisementGte: null,
  daysInAdvertisementLte: null,
  transmission:
    priceReport?.vehicle && !isTransmissionExcluded(priceReport.vehicle.transmission)
      ? [priceReport.vehicle.transmission]
      : [],
  fuelType: priceReport?.vehicle ? [priceReport.vehicle.fuelType] : [],
  powerGte: priceReport?.vehicle?.maxPower ?? priceReport?.vehicle?.power,
  powerLte: priceReport?.vehicle?.maxPower ?? priceReport?.vehicle?.power,
  bodyStyle: getDefaultBodyStyle(priceReport?.vehicle?.bodyStyle),
  drive:
    priceReport?.vehicle &&
    priceReport.vehicle.drive !== 'DRIVE_REAR' &&
    priceReport.vehicle.drive !== 'DRIVE_FRONT'
      ? [priceReport.vehicle.drive]
      : ['DRIVE_4X2'],
  trimLevels: [],
  featureScoreGte: null,
  featureScoreLte: null,
  sellerType: [],
  server: getDefaultServer(priceReport),
});
